import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Send, Copy, Loader2, X, Sparkles, PoundSterling } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import VoiceButton from "@/components/VoiceButton";

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const areaCls = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const DEFAULT_TERMS = "50% deposit secures the dates; balance due 7 days before the event. Cancellations within 14 days of the event are charged in full. Cover is subject to staff availability until confirmed.";

const emptyForm = {
  client_id: "", client_name: "", contact_email: "", event_id: "", event_name: "", overview: "",
  schedule: [], pricing: [], terms: DEFAULT_TERMS, quote_note: "",
  last_year_total: "", last_year_note: "",
  discount_on: false, discount_type: "percent", discount_value: "",
  theme: null, theme_prompt: "",
  event_type: "", requirements: "", extras: [], suggestions: "", show_suppliers: true,
  deposit_amount: "", deposit_due: "",
};

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600",
  sent: "bg-blue-50 text-[#005EB8]",
  changes_requested: "bg-amber-50 text-amber-700",
  accepted: "bg-emerald-50 text-[#007F3B]",
};

const GRADE_LABELS = { first_aider: "First Aider", level_3: "Level 3", level_4: "Level 4", technician: "Technician", paramedic: "Paramedic", doctor: "Doctor" };

const TODAY = new Date().toISOString().slice(0, 10);
const upcomingOnly = (list) => (list || [])
  .filter((e) => e.id && (!e.event_date || e.event_date >= TODAY))
  .sort((a, b) => (a.event_date || "9999").localeCompare(b.event_date || "9999"));

const blankDay = (label, kind = "day", date = "", end_date = "") => ({
  label, kind, date, end_date, times: kind === "night" ? "18:00 – 06:00" : "", cover: "", notes: "", included: "",
  price: "", hours: "", day_hours: "", night_hours: "", rates: {}, day_rates: {}, night_rates: {},
  vehicle_id: "", vehicle_name: "", vehicles: [], equipment: [], staffing: {},
});

const EVENT_TYPES = ["Music festival", "Car / motorsport show", "Country fair", "Agricultural show", "Sporting event",
  "Fireworks display", "Christmas / winter market", "Food & drink festival", "Community carnival", "Equestrian event", "Other"];

const EQUIPMENT_PRESETS = [
  "Gazebo 3×3m", "Gazebo 3×4.5m", "Gazebo 4×8m", "Gazebo 3×6m", "Gazebo 6×3m heavy-duty",
  "Marquee 3×6m", "Marquee 5×6m", "Marquee 6×6m", "Marquee 6×12m", "Marquee 9×9m", "Marquee 9×12m", "Marquee 9×21m", "Marquee 12×12m",
  "Stretch tent 10×15m", "Inflatable tent", "Treatment tent", "Welfare unit",
];

const FieldTools = ({ value, onChange, testid, section }) => {
  const [busy, setBusy] = useState(false);
  const polish = async () => {
    if (!value.trim()) { toast.error("Say or type the rough words first, then let AI tidy them up"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/polish", { text: value, context: `client proposal — ${section}`, mode: "proposal" });
      onChange(data.polished);
      toast.success("Tidied up");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <VoiceButton testid={`${testid}-mic`} onTranscript={(t) => onChange(value ? `${value} ${t}` : t)} />
      <button type="button" onClick={polish} disabled={busy} data-testid={`${testid}-ai`}
        title="AI tidy-up — rewrites your rough words professionally"
        className="h-7 w-7 rounded-full grid place-items-center bg-slate-100 text-slate-500 hover:bg-sky-100 hover:text-[#005EB8] disabled:opacity-50 transition-colors shrink-0">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
};

export default function AdminProposals() {
  const [list, setList] = useState(null);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const [theming, setTheming] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [autoSavedAt, setAutoSavedAt] = useState("");
  const formRef = useRef(form);
  formRef.current = form;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const lastSavedRef = useRef("");

  const buildPayload = (f) => {
    const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
    const cleanLine = (l) => ({ description: l.description || "", qty: num(l.qty), unit_price: num(l.unit_price), per: l.per || "" });
    const cleanDay = (d) => ({ ...d, price: num(d.price), hours: num(d.hours), day_hours: num(d.day_hours), night_hours: num(d.night_hours) });
    return {
      client_id: f.client_id, client_name: f.client_name, contact_email: f.contact_email,
      event_id: f.event_id, event_name: f.event_name, overview: f.overview,
      schedule: f.schedule.map(cleanDay), pricing: f.pricing.map(cleanLine),
      terms: f.terms, quote_note: f.quote_note,
      last_year_total: f.last_year_total === "" ? null : num(f.last_year_total),
      last_year_note: f.last_year_note,
      discount_type: f.discount_on ? f.discount_type : "",
      discount_value: f.discount_on ? num(f.discount_value) : 0,
      deposit_amount: num(f.deposit_amount), deposit_due: f.deposit_due,
      theme: f.theme || {},
      event_type: f.event_type, requirements: f.requirements, extras: f.extras.map(cleanLine),
      suggestions: f.suggestions, show_suppliers: f.show_suppliers,
    };
  };

  useEffect(() => {
    if (!editing) { lastSavedRef.current = ""; setAutoSavedAt(""); return; }
    const t = setInterval(async () => {
      const f = formRef.current;
      const ed = editingRef.current;
      if (!ed || !(f.client_name || f.event_name)) return;
      const snap = JSON.stringify([ed.id, f]);
      if (snap === lastSavedRef.current) return;
      try {
        let id = ed.id;
        if (id) {
          await api.put(`/proposals/${id}`, buildPayload(f));
        } else {
          const { data } = await api.post("/proposals", buildPayload(f));
          id = data.id;
          setEditing((e) => (e ? { ...e, id } : e));
        }
        lastSavedRef.current = JSON.stringify([id, f]);
        setAutoSavedAt(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
      } catch { /* silent — manual Save still surfaces errors */ }
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const load = async () => {
    try {
      const { data } = await api.get("/proposals");
      setList(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
    api.get("/events").then(({ data }) => setEvents(upcomingOnly(data))).catch(() => {});
    api.get("/vehicles").then(({ data }) => setVehicles(data || [])).catch(() => {});
  }, []);

  const daysTotal = form.schedule.reduce((a, d) => a + (Number(d.price) || 0), 0);
  const linesTotal = form.pricing.reduce((a, l) => a + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const extrasTotal = form.extras.reduce((a, l) => a + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const subtotal = daysTotal + linesTotal + extrasTotal;
  const discountAmt = form.discount_on
    ? Math.min(form.discount_type === "percent" ? subtotal * ((Number(form.discount_value) || 0) / 100) : (Number(form.discount_value) || 0), subtotal)
    : 0;
  const total = subtotal - discountAmt;
  const lastYear = Number(form.last_year_total) || 0;

  const openNew = () => { setForm(emptyForm); setRangeStart(""); setRangeEnd(""); setEditing({ id: null }); };
  const openEdit = (p) => {
    setForm({ client_id: p.client_id, client_name: p.client_name, contact_email: p.contact_email, event_id: p.event_id || "", event_name: p.event_name,
              overview: p.overview, schedule: (p.schedule || []).map((d) => ({ ...d,
                vehicles: d.vehicles || (d.vehicle_id ? [{ id: d.vehicle_id, name: d.vehicle_name }] : []),
                equipment: d.equipment || [] })), pricing: p.pricing || [], terms: p.terms || DEFAULT_TERMS, quote_note: p.quote_note || "",
              last_year_total: p.last_year_total ?? "", last_year_note: p.last_year_note || "",
              discount_on: (p.discount_amount || 0) > 0, discount_type: p.discount_type || "percent", discount_value: p.discount_value || "",
              theme: p.theme && p.theme.headline ? p.theme : null, theme_prompt: "",
              event_type: p.event_type || "", requirements: p.requirements || "", extras: p.extras || [],
              suggestions: p.suggestions || "", show_suppliers: p.show_suppliers !== false,
              deposit_amount: p.deposit_amount || "", deposit_due: p.deposit_due || "" });
    setEditing({ id: p.id });
  };
  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (!c) { setForm((f) => ({ ...f, client_id: "", client_name: "", contact_email: "" })); return; }
    const mainCt = (c.contacts || []).find((x) => x.is_main && (x.email || "").trim());
    const anyCt = (c.contacts || []).find((x) => (x.email || "").trim());
    setForm((f) => ({ ...f, client_id: cid, client_name: c.name, contact_email: ((mainCt || anyCt || {}).email || c.email || "").trim() }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (editing.id) await api.put(`/proposals/${editing.id}`, payload);
      else await api.post("/proposals", payload);
      toast.success("Proposal saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const generateTheme = async () => {
    if (!form.theme_prompt.trim()) { toast.error("Describe the theme you want first"); return; }
    setTheming(true);
    try {
      const { data } = await api.post("/proposals/ai-theme", { prompt: form.theme_prompt.trim(), event_name: form.event_name, client_name: form.client_name, event_type: form.event_type });
      setForm((f) => ({ ...f, theme: data }));
      toast.success(data.image ? "Theme & artwork generated" : "Theme generated (artwork unavailable this time)");
    } catch (err) { toast.error(formatApiError(err)); }
    setTheming(false);
  };

  const send = async () => {
    if (!sendTo.trim()) { toast.error("Add an email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/proposals/${sendFor.id}/send`, { to_email: sendTo.trim() });
      toast.success(data.message);
      setSendFor(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  const markDepositPaid = async (p) => {
    if (!window.confirm(`Mark the £${Number(p.deposit_amount).toFixed(2)} deposit as paid and email the balance invoice for £${Math.max((p.total || 0) - (p.deposit_amount || 0), 0).toFixed(2)}?`)) return;
    try {
      const { data } = await api.post(`/proposals/${p.id}/deposit-paid`);
      toast.success(data.message);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete the proposal for ${p.event_name || p.client_name}?`)) return;
    try {
      await api.delete(`/proposals/${p.id}`);
      toast.success("Proposal deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing) {
    const setDay = (i, k, v) => setForm((f) => ({ ...f, schedule: f.schedule.map((d, j) => (j === i ? { ...d, [k]: v } : d)) }));
    const buildDays = (start, end) => {
      if (!start || !end || end < start) return;
      setForm((f) => {
        const have = new Set(f.schedule.map((d) => `${d.kind || "day"}|${d.date}`));
        const rows = [];
        const cursor = new Date(`${start}T00:00:00`);
        const last = new Date(`${end}T00:00:00`);
        let dayN = f.schedule.filter((d) => (d.kind || "day") === "day").length;
        let nightN = f.schedule.filter((d) => d.kind === "night").length;
        while (cursor <= last && rows.length < 124) {
          const iso = cursor.toISOString().slice(0, 10);
          const next = new Date(cursor);
          next.setDate(next.getDate() + 1);
          const nextIso = next.toISOString().slice(0, 10);
          if (!have.has(`day|${iso}`)) {
            dayN += 1;
            rows.push(blankDay(`Day ${dayN}`, "day", iso));
          }
          if (iso !== end && !have.has(`night|${iso}`)) {
            nightN += 1;
            rows.push(blankDay(`Night ${nightN}`, "night", iso, nextIso));
          }
          cursor.setDate(cursor.getDate() + 1);
        }
        return rows.length ? { ...f, schedule: [...f.schedule, ...rows] } : f;
      });
    };
    const changeCalc = (i, d, patch) => {
      const nd = { ...d, ...patch };
      const dayH = Number(nd.day_hours) || 0;
      const nightH = Number(nd.night_hours) || 0;
      let sum = 0, any = false;
      for (const [g, n] of Object.entries(nd.staffing || {})) {
        const c = Number(n) || 0;
        if (!c) continue;
        const dr = Number((nd.day_rates || {})[g]) || 0;
        const nr = Number((nd.night_rates || {})[g]) || 0;
        if (dr > 0 || nr > 0) { sum += c * (dayH * dr + nightH * nr); any = true; }
      }
      if ((dayH || nightH) && any) patch.price = Math.round(sum * 100) / 100;
      setForm((f) => ({ ...f, schedule: f.schedule.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
    };
    const setEquip = (i, j, patch) => setForm((f) => {
      const prev = f.schedule[i]?.equipment?.[j] || {};
      return {
        ...f,
        schedule: f.schedule.map((d, di) => {
          if (di !== i && !(i === 0 && di > 0)) return d;
          const eq = (d.equipment || []).map((x, k) => {
            if (k !== j) return x;
            if (di === i) return { ...x, ...patch };
            return x.name === prev.name && String(x.qty) === String(prev.qty) ? { ...x, ...patch } : x;
          });
          return { ...d, equipment: eq };
        }),
      };
    });
    const addEquip = (i) => setForm((f) => ({
      ...f,
      schedule: f.schedule.map((d, di) => (i === 0 || di === i) ? { ...d, equipment: [...(d.equipment || []), { name: "", qty: 1 }] } : d),
    }));
    const setLine = (i, k, v) => setForm((f) => ({ ...f, pricing: f.pricing.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
    return (
      <div className="min-h-screen bg-[#F4F7FA]" data-testid="proposal-editor">
        <AdminHeader title={editing.id ? "Edit proposal" : "New proposal"} testid="proposal-editor-title" backTo="/admin/proposals" backTestid="proposal-editor-back" eyebrow="WEMS Portal" />
        <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4 pb-24">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">Client & event</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <select value={form.client_id} onChange={(e) => pickClient(e.target.value)} className={inputCls} data-testid="proposal-client">
                <option value="">Pick a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} placeholder="Client / organisation name" className={inputCls} data-testid="proposal-client-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={form.event_name} onChange={(e) => setForm((f) => ({ ...f, event_name: e.target.value }))} placeholder="Event name (e.g. Summer Festival 2026)" className={inputCls} data-testid="proposal-event-name" />
              <input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} placeholder="Contact email (auto-filled from client)" className={inputCls} data-testid="proposal-contact-email" />
            </div>
            <select value={form.event_id} onChange={(e) => {
                const ev = events.find((x) => x.id === e.target.value);
                setForm((f) => ({ ...f, event_id: e.target.value, event_name: f.event_name || (ev ? ev.name : "") }));
                if (ev?.event_date) {
                  const end = ev.end_date && ev.end_date > ev.event_date ? ev.end_date : ev.event_date;
                  setRangeStart(ev.event_date);
                  setRangeEnd(end);
                  buildDays(ev.event_date, end);
                }
              }} className={inputCls} data-testid="proposal-event-link">
              <option value="">Link a calendar event (optional)…</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` — ${ev.event_date}` : ""}</option>)}
            </select>
            {form.event_id && (
              <p className="text-[11px] text-slate-500" data-testid="proposal-event-linked-note">
                Linked to that booking — when the client accepts, it updates this calendar event instead of duplicating its date.
              </p>
            )}
            <select value={form.event_type} onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))} className={inputCls} data-testid="proposal-event-type">
              <option value="">Type of event (guides the AI theme)…</option>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Event start date</span>
                <input type="date" value={rangeStart} onChange={(e) => { setRangeStart(e.target.value); buildDays(e.target.value, rangeEnd); }} className={inputCls} data-testid="proposal-range-start" />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Event end date</span>
                <input type="date" value={rangeEnd} min={rangeStart || undefined} onChange={(e) => { setRangeEnd(e.target.value); buildDays(rangeStart, e.target.value); }} className={inputCls} data-testid="proposal-range-end" />
              </label>
            </div>
            <p className="text-[11px] text-slate-400">Pick both dates and every day of the event appears in the schedule below, dates already filled in — then fill out each day.</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">1 · Proposal</h2>
              <FieldTools value={form.overview} onChange={(v) => setForm((f) => ({ ...f, overview: v }))} testid="proposal-overview-tools" section="overview" />
            </div>
            <textarea rows={5} value={form.overview} onChange={(e) => setForm((f) => ({ ...f, overview: e.target.value }))}
              placeholder="Your overview — what we'll provide, why us, how we'll run it…" className={areaCls} data-testid="proposal-overview" />
            <p className="text-[11px] text-slate-400">Tip: start a line with "- " and it becomes a bullet point on the client's page. Works in the included / requirements / suggestions boxes too.</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-schedule">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">2 · Schedule of cover</h2>
            {form.schedule.map((d, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`proposal-day-${i}`}>
                <div className="grid sm:grid-cols-5 gap-2">
                  <select value={d.kind || "day"} onChange={(e) => {
                      const kind = e.target.value;
                      const m = (d.label || "").match(/^(Day|Night|24h)\s*(\d+)/i);
                      const patch = { kind };
                      if (m || !d.label) patch.label = `${kind === "night" ? "Night" : kind === "24h" ? "24h" : "Day"} ${m ? m[2] : i + 1}`;
                      setForm((f) => ({ ...f, schedule: f.schedule.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
                    }} className={inputCls} title="Day cover or night cover" data-testid={`proposal-day-kind-${i}`}>
                    <option value="day">Day</option>
                    <option value="night">Night</option>
                    <option value="24h">24-hour</option>
                  </select>
                  <input value={d.label} onChange={(e) => setDay(i, "label", e.target.value)} placeholder="Day 1 — Friday" className={inputCls} data-testid={`proposal-day-label-${i}`} />
                  <label className="block">
                    <span className="text-[9px] font-bold uppercase text-slate-400">Start date</span>
                    <input type="date" value={d.date} onChange={(e) => setDay(i, "date", e.target.value)} className={inputCls} data-testid={`proposal-day-date-${i}`} />
                  </label>
                  <label className="block">
                    <span className="text-[9px] font-bold uppercase text-slate-400">End date (if overnight)</span>
                    <input type="date" value={d.end_date || ""} onChange={(e) => setDay(i, "end_date", e.target.value)} min={d.date || undefined} className={inputCls} data-testid={`proposal-day-end-${i}`} />
                  </label>
                  <input value={d.times} onChange={(e) => setDay(i, "times", e.target.value)} placeholder="18:00 – 06:00 (night cover)" className={inputCls} data-testid={`proposal-day-times-${i}`} />
                </div>
                <div className="flex gap-2">
                  <input value={d.cover} onChange={(e) => setDay(i, "cover", e.target.value)} placeholder="Cover — e.g. 2 responders + ambulance on site" className={inputCls} data-testid={`proposal-day-cover-${i}`} />
                  {i > 0 && (
                    <button type="button" onClick={() => setForm((f) => {
                        const p = f.schedule[i - 1];
                        const copy = { kind: p.kind, times: p.times, cover: p.cover, notes: p.notes, included: p.included, price: p.price,
                          hours: p.hours, day_hours: p.day_hours, night_hours: p.night_hours,
                          rates: { ...(p.rates || {}) }, day_rates: { ...(p.day_rates || {}) }, night_rates: { ...(p.night_rates || {}) },
                          vehicle_id: p.vehicle_id, vehicle_name: p.vehicle_name,
                          vehicles: (p.vehicles || []).map((v) => ({ ...v })), equipment: (p.equipment || []).map((x) => ({ ...x })),
                          staffing: { ...(p.staffing || {}) } };
                        return { ...f, schedule: f.schedule.map((x, j) => (j === i ? { ...x, ...copy } : x)) };
                      })}
                      className="h-11 px-3 rounded-lg border border-[#005EB8] text-[#005EB8] hover:bg-sky-50 text-[11px] font-bold shrink-0 transition-colors"
                      title="Copy everything from the row above (staff, rates, vehicles, kit, price) — date stays"
                      data-testid={`proposal-day-same-${i}`}>Same as previous</button>
                  )}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, schedule: f.schedule.filter((_, j) => j !== i) }))} aria-label="Remove day"
                    className="h-11 w-10 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0" data-testid={`proposal-day-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500">What's included this day</span>
                  <FieldTools value={d.included || ""} onChange={(v) => setDay(i, "included", v)} testid={`proposal-day-tools-${i}`} section="what's included" />
                </div>
                <textarea rows={4} value={d.included || ""} onChange={(e) => setDay(i, "included", e.target.value)}
                  placeholder="What you're putting in this day — kit, staff, vehicle, meals, overnight stay…" className={areaCls} data-testid={`proposal-day-included-${i}`} />
                <div className="grid sm:grid-cols-2 gap-2 items-start">
                  <div>
                    <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Vehicles this day (tick all that apply)</span>
                    {vehicles.length === 0 && <p className="text-[11px] text-slate-400">No vehicles yet — add them under Calendar → Vehicles.</p>}
                    <div className="flex flex-wrap gap-1.5" data-testid={`proposal-day-vehicles-${i}`}>
                      {vehicles.map((v) => {
                        const vid = String(v.id || v._id);
                        const on = (d.vehicles || []).some((x) => x.id === vid);
                        return (
                          <button key={vid} type="button"
                            onClick={() => setDay(i, "vehicles", on ? (d.vehicles || []).filter((x) => x.id !== vid) : [...(d.vehicles || []), { id: vid, name: v.name }])}
                            className={`h-8 px-3 rounded-full border text-xs font-bold transition-colors ${on ? "bg-[#005EB8] border-[#005EB8] text-white" : "border-slate-300 text-slate-600 hover:border-[#005EB8]"}`}
                            data-testid={`proposal-day-vehicle-${i}-${vid}`}>{v.name}</button>
                        );
                      })}
                    </div>
                    <span className="text-[11px] font-semibold text-slate-500 mt-2 mb-1 block">Tents / marquees & kit</span>
                    {(d.equipment || []).map((eq, j) => (
                      <div key={j} className="flex gap-1.5 mb-1.5">
                        {eq.custom ? (
                          <input value={eq.name} onChange={(e) => setEquip(i, j, { name: e.target.value })}
                            placeholder="Custom — e.g. Marquee 7×14m" className={`${inputCls} h-9`} data-testid={`proposal-day-equip-name-${i}-${j}`} />
                        ) : (
                          <select value={EQUIPMENT_PRESETS.includes(eq.name) ? eq.name : ""}
                            onChange={(e) => {
                              if (e.target.value === "__custom") setEquip(i, j, { custom: true, name: "" });
                              else setEquip(i, j, { name: e.target.value, custom: false });
                            }}
                            className={`${inputCls} h-9`} data-testid={`proposal-day-equip-name-${i}-${j}`}>
                            <option value="">Pick tent / marquee / kit…</option>
                            {EQUIPMENT_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                            <option value="__custom">Custom / other…</option>
                          </select>
                        )}
                        <input type="number" min="1" value={eq.qty} onChange={(e) => setEquip(i, j, { qty: e.target.value })}
                          className={`${inputCls} h-9`} style={{ width: "4rem" }} title="How many" data-testid={`proposal-day-equip-qty-${i}-${j}`} />
                        <button type="button" onClick={() => setDay(i, "equipment", (d.equipment || []).filter((_, k) => k !== j))} aria-label="Remove equipment"
                          className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0" data-testid={`proposal-day-equip-del-${i}-${j}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addEquip(i)}
                      className="h-8 px-2.5 rounded-lg border border-slate-300 text-[#005EB8] text-[11px] font-bold inline-flex items-center gap-1" data-testid={`proposal-day-equip-add-${i}`}>
                      <Plus className="h-3 w-3" /> Add tent / marquee / kit
                    </button>
                    {i === 0 && form.schedule.length > 1 && (
                      <span className="text-[10px] text-slate-400 block mt-1">Added on Day 1? It appears on every day — delete it on any day that doesn't need it.</span>
                    )}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-[11px] font-semibold text-slate-500">Staff needed — how many, day £/hr & night £/hr</span>
                      <span className="text-[11px] text-slate-400 ml-auto flex items-center gap-1 shrink-0">
                        Day hrs
                        <input type="number" min="0" step="0.5" value={d.day_hours ?? ""} onChange={(e) => changeCalc(i, d, { day_hours: e.target.value })}
                          placeholder="12" title="Daytime hours" className="w-14 h-7 rounded-lg border border-slate-300 px-1 text-center text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
                          data-testid={`proposal-day-dayhrs-${i}`} />
                        Night hrs
                        <input type="number" min="0" step="0.5" value={d.night_hours ?? ""} onChange={(e) => changeCalc(i, d, { night_hours: e.target.value })}
                          placeholder="0" title="Night-time hours" className="w-14 h-7 rounded-lg border border-slate-300 px-1 text-center text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
                          data-testid={`proposal-day-nighthrs-${i}`} />
                      </span>
                    </div>
                    <div className="grid grid-cols-6 gap-1">
                      {Object.entries(GRADE_LABELS).map(([g, label]) => (
                        <label key={g} className="block text-center">
                          <span className="text-[9px] font-bold uppercase text-slate-400 block truncate" title={label}>{label}</span>
                          <input type="number" min="0" value={(d.staffing || {})[g] ?? ""}
                            onChange={(e) => changeCalc(i, d, { staffing: { ...(d.staffing || {}), [g]: e.target.value } })}
                            placeholder="0" title={`${label} — how many`} className="w-full h-9 rounded-lg border border-slate-300 px-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
                            data-testid={`proposal-day-staff-${g}-${i}`} />
                          <input type="number" min="0" step="0.5" value={(d.day_rates || {})[g] ?? ""}
                            onChange={(e) => changeCalc(i, d, { day_rates: { ...(d.day_rates || {}), [g]: e.target.value } })}
                            placeholder="Day £" title={`${label} — daytime £ per hour`} className="w-full h-7 rounded-lg border border-slate-200 px-1 text-center text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8] mt-0.5"
                            data-testid={`proposal-day-dayrate-${g}-${i}`} />
                          <input type="number" min="0" step="0.5" value={(d.night_rates || {})[g] ?? ""}
                            onChange={(e) => changeCalc(i, d, { night_rates: { ...(d.night_rates || {}), [g]: e.target.value } })}
                            placeholder="Night £" title={`${label} — night-time £ per hour`} className="w-full h-7 rounded-lg border border-slate-200 px-1 text-center text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8] mt-0.5"
                            data-testid={`proposal-day-nightrate-${g}-${i}`} />
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                  <span className="text-[11px] font-semibold text-slate-500">Price for this day <span className="font-normal text-slate-400">— auto-fills from hours × staff rates, or type your own</span></span>
                  <input type="number" min="0" step="0.01" value={d.price ?? ""} onChange={(e) => setDay(i, "price", e.target.value)}
                    placeholder="£ 0.00" className={`${inputCls} text-right font-bold`} style={{ width: "8rem" }} data-testid={`proposal-day-price-${i}`} />
                </div>
              </div>
            ))}
            <button type="button" onClick={() => setForm((f) => ({ ...f, schedule: [...f.schedule, blankDay(`Day ${f.schedule.filter((d) => (d.kind || "day") === "day").length + 1}`)] }))}
              className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5" data-testid="proposal-day-add">
              <Plus className="h-3.5 w-3.5" /> Add a day / shift
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-pricing">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">3 · Pricing</h2>
            {daysTotal > 0 && (
              <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="proposal-days-subtotal">
                <p className="text-xs font-semibold text-slate-600">{form.schedule.filter((d) => Number(d.price) > 0).length} day(s) priced in the schedule above</p>
                <p className="text-sm font-bold text-[#0F2547]">£{daysTotal.toFixed(2)}</p>
              </div>
            )}
            <p className="text-[11px] text-slate-400">Extra lines — anything not tied to a single day. Rate per day / per night / each × how many — it all adds up in the total:</p>
            {form.pricing.map((l, i) => (
              <div key={i} className="flex gap-2" data-testid={`proposal-line-${i}`}>
                <input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Description — e.g. Night cover, 2 responders" className={inputCls} data-testid={`proposal-line-desc-${i}`} />
                <input type="number" min="0" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} placeholder="How many" title="How many" className={inputCls} style={{ width: "6rem" }} data-testid={`proposal-line-qty-${i}`} />
                <select value={l.per || ""} onChange={(e) => setLine(i, "per", e.target.value)} title="Rate unit" className={inputCls} style={{ width: "7rem" }} data-testid={`proposal-line-per-${i}`}>
                  <option value="">each</option>
                  <option value="day">per day</option>
                  <option value="night">per night</option>
                  <option value="shift">per shift</option>
                  <option value="person">per person</option>
                </select>
                <input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} placeholder="£ rate" title="£ rate" className={inputCls} style={{ width: "7rem" }} data-testid={`proposal-line-price-${i}`} />
                <button type="button" onClick={() => setForm((f) => ({ ...f, pricing: f.pricing.filter((_, j) => j !== i) }))} aria-label="Remove line"
                  className="h-11 w-10 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0" data-testid={`proposal-line-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setForm((f) => ({ ...f, pricing: [...f.pricing, { description: "", qty: 1, per: "", unit_price: 0 }] }))}
              className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5" data-testid="proposal-line-add">
              <Plus className="h-3.5 w-3.5" /> Add a price line
            </button>

            {!form.discount_on ? (
              <button type="button" onClick={() => setForm((f) => ({ ...f, discount_on: true }))}
                className="text-xs font-bold text-[#005EB8] hover:underline text-left" data-testid="proposal-discount-apply">+ Apply discount</button>
            ) : (
              <div className="rounded-xl border border-slate-200 p-3 space-y-1" data-testid="proposal-discount-section">
                <span className="text-[11px] font-semibold text-slate-500 block">Discount amount</span>
                <div className="grid grid-cols-[1fr_auto_auto] gap-2">
                  <input type="number" min="0" step="0.01" value={form.discount_value}
                    onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                    placeholder={form.discount_type === "percent" ? "e.g. 10" : "e.g. 100"}
                    className={inputCls} data-testid="proposal-discount-value" />
                  <select value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
                    className={`${inputCls} shrink-0`} style={{ width: "5rem" }} data-testid="proposal-discount-type">
                    <option value="percent">%</option>
                    <option value="fixed">£</option>
                  </select>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, discount_on: false, discount_value: "" }))}
                    className="h-11 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 text-xs font-bold shrink-0" data-testid="proposal-discount-remove">Remove</button>
                </div>
              </div>
            )}

            <div className="rounded-xl bg-[#F4F7FA] border border-slate-200 px-4 py-3 space-y-1">
              {discountAmt > 0 && (
                <div className="flex items-center justify-between" data-testid="proposal-discount-summary">
                  <span className="text-xs font-semibold text-slate-500">Subtotal £{subtotal.toFixed(2)} − discount</span>
                  <span className="text-xs font-bold text-[#D5281B]">−£{discountAmt.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-600">Total</span>
                <span className="text-lg font-extrabold text-[#0F2547]" data-testid="proposal-total">£{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-deposit">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">Deposit on acceptance</h2>
            <p className="text-[11px] text-slate-400">The moment the client accepts, the deposit invoice is emailed to them as due now. When the money lands, mark it paid on the proposal card and the balance invoice goes out automatically.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Deposit amount (£) — 0 = no deposit</span>
                <input type="number" min="0" step="0.01" value={form.deposit_amount}
                  onChange={(e) => setForm((f) => ({ ...f, deposit_amount: e.target.value }))}
                  placeholder="e.g. 500" className={inputCls} data-testid="proposal-deposit-amount" />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Deposit due date (blank = due now)</span>
                <input type="date" value={form.deposit_due} onChange={(e) => setForm((f) => ({ ...f, deposit_due: e.target.value }))}
                  className={inputCls} data-testid="proposal-deposit-due" />
              </label>
            </div>
            {Number(form.deposit_amount) > 0 && total > 0 && (
              <p className="text-xs font-semibold text-[#0F2547] rounded-lg bg-sky-50 border border-sky-200 px-3 py-2" data-testid="proposal-deposit-preview">
                Client pays £{(Number(form.deposit_amount) || 0).toFixed(2)} on acceptance · the balance of £{Math.max(total - (Number(form.deposit_amount) || 0), 0).toFixed(2)} is invoiced when you mark the deposit as paid.
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-extras">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">Additional costs</h2>
            <p className="text-[11px] text-slate-400">Extras you pass on to the client — Wi-Fi, food, radio hire and so on. Added into the total:</p>
            {form.extras.map((l, i) => (
              <div key={i} className="flex gap-2" data-testid={`proposal-extra-${i}`}>
                <input value={l.description} onChange={(e) => setForm((f) => ({ ...f, extras: f.extras.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) }))} placeholder="e.g. Radio hire" className={inputCls} data-testid={`proposal-extra-desc-${i}`} />
                <input type="number" min="0" value={l.qty} onChange={(e) => setForm((f) => ({ ...f, extras: f.extras.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)) }))} placeholder="How many" title="How many" className={inputCls} style={{ width: "6rem" }} data-testid={`proposal-extra-qty-${i}`} />
                <input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setForm((f) => ({ ...f, extras: f.extras.map((x, j) => (j === i ? { ...x, unit_price: e.target.value } : x)) }))} placeholder="£ each" title="£ each" className={inputCls} style={{ width: "7rem" }} data-testid={`proposal-extra-price-${i}`} />
                <button type="button" onClick={() => setForm((f) => ({ ...f, extras: f.extras.filter((_, j) => j !== i) }))} aria-label="Remove extra"
                  className="h-11 w-10 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0" data-testid={`proposal-extra-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setForm((f) => ({ ...f, extras: [...f.extras, { description: "", qty: 1, per: "", unit_price: 0 }] }))}
              className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5" data-testid="proposal-extra-add">
              <Plus className="h-3.5 w-3.5" /> Add an extra cost
            </button>
            {extrasTotal > 0 && <p className="text-xs font-bold text-[#0F2547]" data-testid="proposal-extras-subtotal">Extras subtotal: £{extrasTotal.toFixed(2)}</p>}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-requirements">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">What we require from them</h2>
              <FieldTools value={form.requirements} onChange={(v) => setForm((f) => ({ ...f, requirements: v }))} testid="proposal-requirements-tools" section="client requirements" />
            </div>
            <textarea rows={4} value={form.requirements} onChange={(e) => setForm((f) => ({ ...f, requirements: e.target.value }))}
              placeholder="e.g. Access to power, hard standing for the ambulance, water access, toilets, meal provision for overnight crew, site passes…" className={areaCls} data-testid="proposal-requirements-input" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-suggestions">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">Suggestions · what we think they should have</h2>
              <FieldTools value={form.suggestions} onChange={(v) => setForm((f) => ({ ...f, suggestions: v }))} testid="proposal-suggestions-tools" section="recommendations" />
            </div>
            <textarea rows={4} value={form.suggestions} onChange={(e) => setForm((f) => ({ ...f, suggestions: e.target.value }))}
              placeholder="e.g. Based on your crowd size we'd suggest adding a second treatment point on Saturday, and a dedicated missing-persons post…" className={areaCls} data-testid="proposal-suggestions-input" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="proposal-suppliers-toggle">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.show_suppliers} onChange={(e) => setForm((f) => ({ ...f, show_suppliers: e.target.checked }))}
                className="h-5 w-5 rounded border-slate-300 text-[#005EB8] focus:ring-[#005EB8]" data-testid="proposal-suppliers-check" />
              <span className="text-sm font-semibold text-[#0F2547]">Show the "suppliers we like to work with" link at the bottom of the client's proposal page</span>
            </label>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-lastyear">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">Last year · only you see this</h2>
            <p className="text-[11px] text-slate-400">Compare against last year's quote — the client never sees this section.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Last year's total (£)</span>
                <input type="number" min="0" step="0.01" value={form.last_year_total}
                  onChange={(e) => setForm((f) => ({ ...f, last_year_total: e.target.value }))}
                  placeholder="e.g. 1800" className={inputCls} data-testid="proposal-lastyear-total" />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Note (e.g. what changed)</span>
                <input value={form.last_year_note} onChange={(e) => setForm((f) => ({ ...f, last_year_note: e.target.value }))}
                  placeholder="e.g. 2025 was 4 days, no night cover" className={inputCls} data-testid="proposal-lastyear-note" />
              </label>
            </div>
            {lastYear > 0 && (
              <p className="text-sm font-bold text-[#0F2547] rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="proposal-yoy-compare">
                Last year £{lastYear.toFixed(2)} → This year £{total.toFixed(2)}
                <span className={total >= lastYear ? "text-[#D5281B]" : "text-[#007F3B]"}>
                  {" "}({total >= lastYear ? "+" : "−"}£{Math.abs(total - lastYear).toFixed(2)} {total >= lastYear ? "extra" : "less"})
                </span>
                {form.last_year_note && <span className="block text-[11px] font-semibold text-slate-500 mt-0.5">{form.last_year_note}</span>}
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">4 · Terms</h2>
            <textarea rows={4} value={form.terms} onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))} className={areaCls} data-testid="proposal-terms" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">5 · Formal quote summary</h2>
            <textarea rows={3} value={form.quote_note} onChange={(e) => setForm((f) => ({ ...f, quote_note: e.target.value }))}
              placeholder="e.g. This quote is valid for 30 days. Total £X for the full 5 days including all night cover." className={areaCls} data-testid="proposal-quote-note" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="proposal-theme">
            <h2 className="font-heading text-sm font-bold text-[#0F2547] uppercase tracking-wide">AI theme · client page design</h2>
            <p className="text-[11px] text-slate-400">Describe the vibe and AI builds a tailored look — headline, colours and custom artwork for their proposal page.</p>
            <div className="flex gap-2">
              <input value={form.theme_prompt} onChange={(e) => setForm((f) => ({ ...f, theme_prompt: e.target.value }))}
                placeholder="e.g. country music festival, summer evenings, family fun" className={inputCls} data-testid="proposal-theme-prompt" />
              <button type="button" onClick={generateTheme} disabled={theming}
                className="h-11 px-4 rounded-lg bg-[#00A499] hover:bg-[#008f85] disabled:opacity-50 text-white text-xs font-bold inline-flex items-center gap-1.5 shrink-0 transition-colors" data-testid="proposal-theme-generate">
                {theming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {theming ? "Designing…" : "Generate"}
              </button>
            </div>
            {form.theme && (
              <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="proposal-theme-preview">
                {form.theme.image && <img src={form.theme.image} alt="" className="w-full h-36 object-cover" />}
                <div className="p-3 space-y-1.5" style={{ backgroundColor: form.theme.secondary_color || "#0F2547" }}>
                  <p className="font-heading font-bold text-white text-sm">{form.theme.headline}</p>
                  <p className="text-xs text-slate-300">{form.theme.tagline}</p>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="h-5 w-5 rounded-full border border-white/30" style={{ backgroundColor: form.theme.accent_color }} />
                    <span className="h-5 w-5 rounded-full border border-white/30" style={{ backgroundColor: form.theme.secondary_color }} />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, theme: null }))}
                      className="ml-auto text-[11px] font-bold text-red-300 hover:text-red-200" data-testid="proposal-theme-remove">Remove theme</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-[11px] text-slate-400" data-testid="proposal-autosave-note">
            Auto-saves every 30 seconds as you work — you can't lose it{autoSavedAt ? ` · last saved ${autoSavedAt}` : ""}
          </p>
          <div className="flex gap-2 sticky bottom-4">
            <button onClick={save} disabled={saving} data-testid="proposal-save"
              className="flex-1 h-12 rounded-xl bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors shadow-lg">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save proposal
            </button>
            <button onClick={() => setEditing(null)} data-testid="proposal-cancel"
              className="h-12 px-5 rounded-xl border border-slate-300 text-sm font-bold text-slate-600 bg-white transition-colors">Cancel</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="proposals-page">
      <AdminHeader title="Proposals" testid="proposals-title" backTo="/admin" backTestid="proposals-back" eyebrow="WEMS Portal" />
      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500">Big jobs only — proposal, schedule, pricing, terms & formal quote. The client accepts online; it pencils the dates on your calendar.</p>
          <button onClick={openNew} data-testid="proposal-new"
            className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-sm font-bold inline-flex items-center gap-2 transition-colors shrink-0">
            <Plus className="h-4 w-4" /> New proposal
          </button>
        </div>
        {list === null && <p className="text-sm text-slate-400">Loading…</p>}
        {list?.length === 0 && <p className="text-sm text-slate-400 bg-white rounded-2xl border border-slate-200 p-6 text-center" data-testid="proposals-empty">No proposals yet — build one for a big job.</p>}
        {list && list.length > 0 && (() => {
          const drafts = list.filter((p) => p.status === "draft");
          const sent = list.filter((p) => p.status === "sent" || p.status === "changes_requested");
          const done = list.filter((p) => p.status === "accepted");
          const renderCard = (p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`proposal-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-heading font-bold text-[#0F2547]">{p.event_name || p.client_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{p.client_name} · £{p.total.toFixed(2)} · {(p.schedule || []).length} day(s)</p>
                {p.status === "accepted" && <p className="text-xs text-[#007F3B] mt-0.5">Accepted by {p.accepted_by}</p>}
                {(p.deposit_amount || 0) > 0 && p.status === "accepted" && (
                  <p className={`text-xs mt-0.5 font-semibold ${p.deposit_paid ? "text-[#007F3B]" : "text-amber-700"}`} data-testid={`proposal-deposit-status-${p.id}`}>
                    {p.deposit_paid
                      ? `Deposit £${Number(p.deposit_amount).toFixed(2)} paid — balance invoice sent`
                      : `Deposit £${Number(p.deposit_amount).toFixed(2)} — invoice ${p.deposit_sent ? "sent, awaiting payment" : "raised (not emailed)"}`}
                  </p>
                )}
                {p.status === "sent" && <p className="text-xs text-slate-400 mt-0.5">Sent to {p.sent_to}</p>}
                {(p.email_opens > 0 || p.email_clicks > 0) && (
                  <p className="text-xs font-semibold text-[#005EB8] mt-0.5" data-testid={`proposal-tracking-${p.id}`}>
                    {p.email_opens > 0 ? `Opened ${p.email_opens}×` : "Not opened yet"}{p.email_clicks > 0 ? ` · link clicked ${p.email_clicks}×` : ""}
                  </p>
                )}
                {(p.queries || []).length > 0 && (
                  <p className="text-xs text-amber-700 mt-0.5" data-testid={`proposal-queries-${p.id}`}>
                    {p.queries.length} question{p.queries.length > 1 ? "s" : ""} from the client — latest: {(p.queries[p.queries.length - 1].comment || p.queries[p.queries.length - 1].items?.[0]?.text || "").slice(0, 90)}
                  </p>
                )}
              </div>
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${STATUS_STYLE[p.status] || STATUS_STYLE.draft}`} data-testid={`proposal-status-${p.id}`}>{p.status.replace(/_/g, " ")}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => { setSendFor(p); setSendTo(p.contact_email || ""); }} data-testid={`proposal-send-${p.id}`}
                className="h-9 px-3 rounded-lg bg-[#00A499] hover:bg-[#008f85] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <Send className="h-3.5 w-3.5" /> Send
              </button>
              <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/proposal/${p.token}`); toast.success("Proposal link copied"); }} data-testid={`proposal-copy-${p.id}`}
                className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <Copy className="h-3.5 w-3.5" /> Copy link
              </button>
              {p.status !== "accepted" && (
                <button onClick={() => openEdit(p)} data-testid={`proposal-edit-${p.id}`}
                  className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
              )}
              <a href={`/proposal/${p.token}`} target="_blank" rel="noreferrer" data-testid={`proposal-view-${p.id}`}
                className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] text-xs font-bold inline-flex items-center transition-colors">Preview</a>
              {p.status === "accepted" && (p.deposit_amount || 0) > 0 && !p.deposit_paid && (
                <button onClick={() => markDepositPaid(p)} data-testid={`proposal-deposit-paid-${p.id}`}
                  className="h-9 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006831] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                  <PoundSterling className="h-3.5 w-3.5" /> Deposit paid — send balance
                </button>
              )}
              <button onClick={() => del(p)} data-testid={`proposal-delete-${p.id}`}
                className="h-9 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
          );
          const Section = ({ title, hint, items, testid }) => items.length > 0 && (
            <section className="space-y-3" data-testid={testid}>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 pt-1">
                {title} · {items.length} <span className="normal-case font-medium text-slate-400">— {hint}</span>
              </h2>
              {items.map(renderCard)}
            </section>
          );
          return (
            <>
              <Section title="Drafts" hint="still working on these" items={drafts} testid="proposals-drafts" />
              <Section title="Sent" hint="with the client — watch for opens, clicks & questions" items={sent} testid="proposals-sent" />
              <Section title="Finished" hint="accepted by the client" items={done} testid="proposals-finished" />
            </>
          );
        })()}
      </main>

      {sendFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSendFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="proposal-send-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-[#0F2547]">Send proposal</h3>
              <button onClick={() => setSendFor(null)} aria-label="Close" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-2">{sendFor.event_name || sendFor.client_name}</p>
            <input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="client@example.com"
              className={inputCls} data-testid="proposal-send-input" />
            <button onClick={send} disabled={sending} data-testid="proposal-send-confirm"
              className="mt-3 w-full h-11 rounded-lg bg-[#00A499] hover:bg-[#008f85] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send proposal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
